import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PlusIcon as Plus, TrashIcon as Trash, FloppyDiskIcon as FloppyDisk, CopySimpleIcon as CopySimple, PaletteIcon as Palette } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { SistemistiPremiSettings } from "@/components/SistemistiPremiSettings";
import { SistemistiIndennitaSettings } from "@/components/SistemistiIndennitaSettings";

const MESI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const MESI_FULL = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

export function SistemistiSettings() {
  const [anno, setAnno] = useState(new Date().getFullYear());
  const [anni, setAnni] = useState([new Date().getFullYear()]);
  const [innerTab, setInnerTab] = useState("configurazione");
  const [cfg, setCfg] = useState(null);
  const [tecnici, setTecnici] = useState([]);
  const [fasce, setFasce] = useState([]);
  const [addTec, setAddTec] = useState("");
  const [saving, setSaving] = useState(false);
  const [nyOpen, setNyOpen] = useState(false);
  const [nyAnno, setNyAnno] = useState(new Date().getFullYear() + 1);
  const [nyMode, setNyMode] = useState("vuoto");
  const [nySrc, setNySrc] = useState(new Date().getFullYear());

  const loadAnni = () => api.get("/reportistica/sistemisti/anni").then(({ data }) => setAnni(data.anni)).catch(() => {});
  useEffect(() => { api.get("/tecnici").then(({ data }) => setTecnici(data)).catch(() => {}); loadAnni(); }, []);
  useEffect(() => {
    api.get(`/reportistica/sistemisti/config?anno=${anno}`).then(({ data }) => setCfg(data)).catch(() => {});
  }, [anno]);
  useEffect(() => { api.get("/reportistica/sistemisti/soglie").then(({ data }) => setFasce(data.fasce)).catch(() => {}); }, []);

  const tecName = (id) => (tecnici.find((t) => t.id === id)?.name) || id;
  const setGl = (m, v) => setCfg((c) => ({ ...c, giorni_lavorativi: { ...c.giorni_lavorativi, [m]: v } }));
  const setUla = (idx, m, v) => setCfg((c) => {
    const tt = [...c.tecnici]; tt[idx] = { ...tt[idx], ula: { ...tt[idx].ula, [m]: v } }; return { ...c, tecnici: tt };
  });
  const addTecnico = () => {
    if (!addTec) return;
    if (cfg.tecnici.some((t) => t.tecnico_id === addTec)) return toast.error("Tecnico già presente per questo anno");
    setCfg((c) => ({ ...c, tecnici: [...c.tecnici, { tecnico_id: addTec, ula: Object.fromEntries(Array.from({ length: 12 }, (_, i) => [String(i + 1), 0])) }] }));
    setAddTec("");
  };
  const removeTecnico = (idx) => setCfg((c) => ({ ...c, tecnici: c.tecnici.filter((_, i) => i !== idx) }));
  const copyForward = (idx, m) => setCfg((c) => {
    const tt = [...c.tecnici]; const val = tt[idx].ula[String(m)]; const ula = { ...tt[idx].ula };
    for (let k = m + 1; k <= 12; k++) ula[String(k)] = val;
    tt[idx] = { ...tt[idx], ula }; return { ...c, tecnici: tt };
  });

  const saveCfg = async () => {
    setSaving(true);
    try {
      const payload = {
        anno, totale_ula: Number(cfg.totale_ula) || 0, prezzo_orario_atteso: Number(cfg.prezzo_orario_atteso) || 0,
        giorni_lavorativi: Object.fromEntries(Object.entries(cfg.giorni_lavorativi).map(([k, v]) => [k, Number(v) || 0])),
        tecnici: cfg.tecnici.map((t) => ({ tecnico_id: t.tecnico_id, ula: Object.fromEntries(Object.entries(t.ula).map(([k, v]) => [k, Number(String(v).replace(",", ".")) || 0])) })),
      };
      const { data } = await api.put("/reportistica/sistemisti/config", payload);
      setCfg(data); toast.success("Configurazione Sistemisti salvata");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const setFascia = (i, k, v) => setFasce((f) => f.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const createYear = async () => {
    const y = parseInt(nyAnno, 10);
    if (Number.isNaN(y) || y < 2000 || y > 2100) return toast.error("Anno non valido");
    try {
      await api.post("/reportistica/sistemisti/anni", { anno: y, modalita: nyMode, anno_sorgente: nyMode === "copia" ? Number(nySrc) : undefined });
      toast.success(`Anno ${y} creato`); await loadAnni(); setAnno(y); setNyOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore creazione anno"); }
  };
  const saveSoglie = async () => {
    try {
      const payload = { fasce: fasce.map((f) => ({ min: f.min === "" ? null : Number(f.min), max: f.max === "" || f.max == null ? null : Number(f.max), colore: f.colore, label: f.label })) };
      const { data } = await api.put("/reportistica/sistemisti/soglie", payload);
      setFasce(data.fasce); toast.success("Soglie rendimento salvate");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio soglie"); }
  };

  if (!cfg) return <div className="text-sm text-muted-foreground">Caricamento…</div>;
  const availTec = tecnici.filter((t) => !cfg.tecnici.some((x) => x.tecnico_id === t.id));

  return (
    <div className="space-y-5" data-testid="sistemisti-settings">
      <div className="flex items-center gap-1 border-b border-border" data-testid="sistemisti-inner-menu">
        <button onClick={() => setInnerTab("configurazione")} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${innerTab === "configurazione" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`} data-testid="inner-tab-configurazione">Configurazione</button>
        <button onClick={() => setInnerTab("premi")} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${innerTab === "premi" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`} data-testid="inner-tab-premi">Premi Performance</button>
        <button onClick={() => setInnerTab("indennita")} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${innerTab === "indennita" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`} data-testid="inner-tab-indennita">Indennità, discrezionali e decurtazioni</button>
        <div className="ml-auto flex items-center gap-3">
          <Label className="text-xs uppercase tracking-wider">Anno</Label>
          <Select value={String(anno)} onValueChange={(v) => setAnno(Number(v))}>
            <SelectTrigger className="w-24 rounded-sm h-8" data-testid="set-anno"><SelectValue /></SelectTrigger>
            <SelectContent>{anni.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="rounded-sm h-8 gap-1" onClick={() => { setNyAnno((anni[0] || new Date().getFullYear()) + 1); setNySrc(anno); setNyOpen(true); }} data-testid="set-new-year-btn"><Plus size={14} /> Nuovo anno</Button>
        </div>
      </div>

      <Dialog open={nyOpen} onOpenChange={setNyOpen}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="set-new-year-dialog">
          <DialogHeader><DialogTitle>Nuovo anno</DialogTitle><DialogDescription>Crea configurazione Sistemisti e Premi per un nuovo anno.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider">Anno da creare</Label>
              <Input type="number" value={nyAnno} onChange={(e) => setNyAnno(e.target.value)} className="mt-1 rounded-sm" data-testid="set-ny-anno" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Modalità</Label>
              <Select value={nyMode} onValueChange={setNyMode}>
                <SelectTrigger className="mt-1 rounded-sm h-9" data-testid="set-ny-mode"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="vuoto">Crea configurazione vuota</SelectItem><SelectItem value="copia">Copia configurazione da un altro anno</SelectItem></SelectContent>
              </Select></div>
            {nyMode === "copia" && <div><Label className="text-xs uppercase tracking-wider">Anno sorgente</Label>
              <Select value={String(nySrc)} onValueChange={(v) => setNySrc(Number(v))}>
                <SelectTrigger className="mt-1 rounded-sm h-9" data-testid="set-ny-src"><SelectValue /></SelectTrigger>
                <SelectContent>{anni.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select></div>}
          </div>
          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setNyOpen(false)}>Annulla</Button><Button className="rounded-sm" onClick={createYear} data-testid="set-ny-save">Crea anno</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {innerTab === "premi" && <SistemistiPremiSettings anno={anno} />}
      {innerTab === "indennita" && <SistemistiIndennitaSettings anno={anno} />}
      {innerTab === "configurazione" && (<>
      <div className="flex items-center justify-end gap-3">
        <Button className="rounded-sm gap-1.5" onClick={saveCfg} disabled={saving} data-testid="save-config"><FloppyDisk size={15} /> {saving ? "Salvataggio…" : "Salva configurazione"}</Button>
      </div>

      {/* 4.1 Config generale annuale */}
      <Card className="rounded-sm border-border shadow-none p-5" data-testid="cfg-generale">
        <h4 className="font-heading font-semibold mb-1">Configurazione generale annuale</h4>
        <p className="text-xs text-muted-foreground mb-4">Valori validi per l'anno {anno}.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider">Totale ULA forza lavoro aziendale</Label>
            <Input type="number" step="0.01" value={cfg.totale_ula} onChange={(e) => setCfg((c) => ({ ...c, totale_ula: e.target.value }))} className="mt-1 rounded-sm" data-testid="cfg-totale-ula" />
            <p className="text-[11px] text-muted-foreground mt-1">Valore manuale, indipendente dalla somma dei tecnici configurati.</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Prezzo orario medio ponderato atteso (€)</Label>
            <Input type="number" step="0.01" value={cfg.prezzo_orario_atteso} onChange={(e) => setCfg((c) => ({ ...c, prezzo_orario_atteso: e.target.value }))} className="mt-1 rounded-sm" data-testid="cfg-prezzo-atteso" />
            <p className="text-[11px] text-muted-foreground mt-1">Uguale per tutti i tecnici, può variare per anno.</p>
          </div>
        </div>
      </Card>

      {/* 4.2 Giorni lavorativi */}
      <Card className="rounded-sm border-border shadow-none p-5" data-testid="cfg-giorni">
        <h4 className="font-heading font-semibold mb-1">Giorni lavorativi mensili</h4>
        <p className="text-xs text-muted-foreground mb-4">Giorni teorici al netto di sabati, domeniche, festività e chiusure aziendali.</p>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
          {MESI.map((m, i) => (
            <div key={i}>
              <Label className="text-[11px] text-muted-foreground">{MESI_FULL[i]}</Label>
              <Input type="number" min={0} value={cfg.giorni_lavorativi[String(i + 1)]} onChange={(e) => setGl(String(i + 1), e.target.value)} className="mt-1 rounded-sm h-8 text-sm" data-testid={`gl-${i + 1}`} />
            </div>
          ))}
        </div>
      </Card>

      {/* 4.3 Tecnici e Peso ULA */}
      <Card className="rounded-sm border-border shadow-none p-5" data-testid="cfg-tecnici">
        <div className="flex items-center justify-between mb-3">
          <div><h4 className="font-heading font-semibold">Tecnici e Peso ULA</h4><p className="text-xs text-muted-foreground">Peso ULA mensile per tecnico (es. 1 / 0,8 / 0,5 / 0).</p></div>
          <div className="flex items-center gap-2">
            <Select value={addTec} onValueChange={setAddTec}>
              <SelectTrigger className="w-52 rounded-sm h-9" data-testid="add-tec-select"><SelectValue placeholder="Seleziona tecnico…" /></SelectTrigger>
              <SelectContent>{availTec.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="outline" className="rounded-sm gap-1.5" onClick={addTecnico} data-testid="add-tec-btn"><Plus size={15} /> Aggiungi</Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs whitespace-nowrap">
            <thead><tr className="text-left border-b border-border bg-secondary/40">
              <th className="px-2 py-2 font-semibold sticky left-0 bg-secondary/40">Tecnico</th>
              {MESI.map((m) => <th key={m} className="px-1 py-2 text-center font-semibold">{m}</th>)}
              <th className="px-2 py-2"></th>
            </tr></thead>
            <tbody>
              {cfg.tecnici.length === 0 && <tr><td colSpan={14} className="px-2 py-6 text-center text-muted-foreground">Nessun tecnico configurato. Aggiungine uno.</td></tr>}
              {cfg.tecnici.map((t, idx) => (
                <tr key={t.tecnico_id} className="border-b border-border/40" data-testid={`tec-row-${t.tecnico_id}`}>
                  <td className="px-2 py-1.5 font-medium sticky left-0 bg-white">{tecName(t.tecnico_id)}</td>
                  {MESI.map((m, i) => (
                    <td key={i} className="px-0.5 py-1 text-center">
                      <div className="flex items-center gap-0.5">
                        <Input value={t.ula[String(i + 1)]} onChange={(e) => setUla(idx, String(i + 1), e.target.value)} className="w-12 h-7 rounded-sm text-center text-xs px-1" data-testid={`ula-${t.tecnico_id}-${i + 1}`} />
                        <button className="text-muted-foreground hover:text-primary" title="Copia sui mesi successivi" onClick={() => copyForward(idx, i + 1)} data-testid={`copy-${t.tecnico_id}-${i + 1}`}><CopySimple size={11} /></button>
                      </div>
                    </td>
                  ))}
                  <td className="px-2 py-1"><button className="text-rose-500 hover:text-rose-700" onClick={() => removeTecnico(idx)} data-testid={`remove-tec-${t.tecnico_id}`}><Trash size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* 4.4 Soglie rendimento */}
      <Card className="rounded-sm border-border shadow-none p-5" data-testid="cfg-soglie">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><Palette size={18} className="text-primary" /><h4 className="font-heading font-semibold">Soglie rendimento e colori</h4></div>
          <Button variant="outline" className="rounded-sm gap-1.5" onClick={saveSoglie} data-testid="save-soglie"><FloppyDisk size={14} /> Salva soglie</Button>
        </div>
        <p className="text-xs text-muted-foreground mb-3">Fasce contigue (min incluso, max escluso). Lascia vuoto min/max per "nessun limite".</p>
        <div className="space-y-2">
          {fasce.map((f, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`fascia-${i}`}>
              <Input type="number" placeholder="min" value={f.min ?? ""} onChange={(e) => setFascia(i, "min", e.target.value)} className="w-24 rounded-sm h-8 text-sm" data-testid={`fascia-min-${i}`} />
              <span className="text-muted-foreground text-xs">≤ % &lt;</span>
              <Input type="number" placeholder="max" value={f.max ?? ""} onChange={(e) => setFascia(i, "max", e.target.value)} className="w-24 rounded-sm h-8 text-sm" data-testid={`fascia-max-${i}`} />
              <input type="color" value={f.colore} onChange={(e) => setFascia(i, "colore", e.target.value)} className="h-8 w-12 rounded-sm border border-border" data-testid={`fascia-colore-${i}`} />
              <Input placeholder="Etichetta" value={f.label} onChange={(e) => setFascia(i, "label", e.target.value)} className="flex-1 rounded-sm h-8 text-sm" data-testid={`fascia-label-${i}`} />
              <span className="px-2 py-1 rounded text-[11px] font-semibold" style={{ color: f.colore }}>{f.label || "esempio"}</span>
            </div>
          ))}
        </div>
      </Card>
      </>)}
    </div>
  );
}
